<template>
  <slot></slot>
  <modal :id="modalId" :title="title" icon="fa-calculator" @shown="onModalShown">
    <form :id="formId" :action="action" method="POST" @submit="loading = true">
      <slot name="hidden"></slot>
      <input type="hidden" name="_token" :value="$csrf_token">
      <input type="hidden" name="_method" :value="method">
      <input type="hidden" name="type_id" :value="transactionType">
      <input type="hidden" name="submit_transaction" value="initial_reserves">

      <div class="form-group row">
        <label for="date" class="col-sm-3 col-form-label">Date & Time:</label>
        <div class="col-sm-9">
          <datepicker
            v-model="fields.date"
            :with-time="true"
            placeholder="Leave blank to use current date/time"
            name="date">
          </datepicker>
        </div>
      </div>

      <div class="form-group row">
        <label for="loss_reserve_adjustment" class="col-sm-3 col-form-label">Initial Loss Reserve:</label>
        <div class="col-sm-9">
          <div class="input-group">
            <div class="input-group-prepend" :class="{'is-invalid': invalid('loss')}">
              <span class="input-group-text"><i class="fa fa-dollar fa-fw"></i></span>
            </div>
            <input type="number" step="0.01" :min="limits.loss_min_adjustment"
                   class="form-control form-control-sm" :class="{'is-invalid': invalid('loss')}"
                   name="loss_reserve_adjustment" v-model="fields.loss" placeholder="Enter loss reserve amount">
          </div>
          <div v-if="invalid('loss')" class="small text-danger">{{ message('loss') }}</div>
          <div v-else class="small text-muted">
            {{ minLossMessage }}
          </div>
        </div>
      </div>

      <div class="form-group row">
        <label for="expense_reserve_adjustment" class="col-sm-3 col-form-label">Initial Expense Reserve:</label>
        <div class="col-sm-9">
          <div class="input-group">
            <div class="input-group-prepend" :class="{'is-invalid': invalid('expense')}">
              <span class="input-group-text"><i class="fa fa-dollar fa-fw"></i></span>
            </div>
            <input type="number" step="0.01" :min="limits.expense_min_adjustment"
                   class="form-control form-control-sm" :class="{'is-invalid': invalid('expense')}"
                   name="expense_reserve_adjustment" v-model="fields.expense" placeholder="Enter expense reserve amount">
          </div>
          <div v-if="invalid('expense')" class="small text-danger">{{ message('expense') }}</div>
          <div v-else class="small text-muted">
            {{ minExpenseMessage }}
          </div>
        </div>
      </div>

      <div v-if="needSubcategory" class="form-group row">
        <label for="subcategory" class="col-sm-3 col-form-label">Subcategory:</label>
        <div class="col-sm-9">
          <select class="custom-select" :class="{'is-invalid': invalid('subcategory')}" v-model="fields.subcategory" name="subcategory" required>
            <option value="">Select subcategory...</option>
            <option v-for="subcategory in Object.entries(subcategoryOptions)" :value="subcategory[0]">{{ subcategory[1] }}</option>
          </select>
          <div v-if="invalid('subcategory')" class="small text-danger">{{ message('subcategory') }}</div>
        </div>
      </div>

      <div class="form-group row">
        <label for="note" class="col-sm-3 col-form-label">Comments:</label>
        <div class="col-sm-9">
          <textarea class="form-control" name="note" v-model="fields.note" style="resize: none;" placeholder="Enter comments related to this transaction here..." rows="4"></textarea>
        </div>
      </div>

    </form>

    <template #footer>
      <button type="button" class="btn btn-default" @click="hideModal" :disabled="loading"><i class="fa fa-fw fa-close"></i> Close</button>

      <a v-if="invalidated" href class="btn btn-warning" @click.prevent="displayValidation"><i class="fa fa-fw fa-save"></i> Save <i class="fa fa-fw fa-exclamation-triangle"></i></a>
      <button v-else type="submit" class="btn btn-primary" :form="formId" :disabled="loading">
        <i v-if="loading" class="fa fa-spinner fa-spin fa-fw"></i>
        <i class="fa fa-fw fa-save"></i> Save
      </button>
    </template>

  </modal>
</template>

<script>
import cims from "../../../cims";
import HasModal from "./../../mixins/HasModal";
import HasValidation from "../../mixins/HasValidation";

export default {
  mixins: [HasModal, HasValidation],

  props: {
    // Modal dialog id.
    modalId: {required: false, default: 'initialReservesModal'},
    // Form action URL.
    action: {type: String, required: true},
    // Form method.
    method: {type: String, default: 'POST'},
    // Modal title.
    title: {type: String, default: "Initial Reserves"},
    transactionType: {type: String, required: true},
    limits: {type: Object, default: () => {
      return {
        'expense_min_adjustment': 0,
        'loss_min_adjustment': 0,
      }}
    },
    id: {type: Number, default: 0},
    review: {type: Boolean, default: false},
    needSubcategory: {type: Boolean, default: false},
    subcategoryOptions: {type: Object, default: () => {}},

    // ### Form fields.
    date: {type: String, default: ''},
    loss: {type: String, default: ''},
    expense: {type: String, default: ''},
    subcategory: {type: String, default: ''},
    note: {type: String, default: ''},
  },

  data() {
    return {
      fields: {
        date: this.date,
        loss: this.loss,
        expense: this.expense,
        subcategory: this.subcategory,
        note: this.note,
      },
      loading: false,
    }
  },

  computed: {

    formId() {
      return this.modalId + "-form"
    },

    rules() {
      return {
        'loss': {
          'required': this.lossNumeric !== 0,
          'min': this.lossNumeric >= this.limits.loss_min_adjustment,
        },
        'expense': {
          'required': this.expenseNumeric !== 0,
          'min': this.isMinExpenseValid,
        },
        'subcategory': {
          'required': (this.needSubcategory && (this.fields.subcategory !== '')) || !this.needSubcategory,
        },
      }
    },

    messages() {
      return {
        'loss.required': 'The initial loss reserve amount is required.',
        'loss.min': this.minLossMessage,
        'expense.required': 'The initial expense reserve amount is required.',
        'expense.min': this.minExpenseMessage,
        'subcategory.required': 'The subcategory is required.',
      }
    },

    lossNumeric() {
      return this.fields.loss === '' ? 0 : parseFloat(this.fields.loss);
    },

    expenseNumeric() {
      return this.fields.expense === '' ? 0 : parseFloat(this.fields.expense);
    },

    minLossMessage() {
      const minimum = this.limits.loss_min_adjustment;
      if (this.lossNumeric >= minimum) return '';
      return 'To maintain a positive final loss reserve, ' + this.minimumAdjustmentMessage(minimum);
    },

    minExpenseMessage() {
      let minimum = this.limits.expense_min_adjustment;
      if (this.needSubcategory && (this.fields.subcategory !== '') && (this.limits.subcategory[this.fields.subcategory].min_adjustment !== undefined) && (this.expenseNumeric !== 0))
        minimum = this.limits.subcategory[this.fields.subcategory].min_adjustment;

      if (this.expenseNumeric >= minimum) return '';

      return 'To maintain a positive final expense reserve' +
        (this.needSubcategory ? (this.fields.subcategory !== '' ? ' (' + (this.subcategoryOptions[this.fields.subcategory] ?? '') + ')' : ' (summary across all subcategories)') : '') + ', ' +
        this.minimumAdjustmentMessage(minimum);
    },

    isMinExpenseValid() {
      if (this.needSubcategory) {
        if (this.fields.subcategory === '') return true;
        if (this.limits.subcategory[this.fields.subcategory].min_adjustment === undefined) return true;
        return this.expenseNumeric >= this.limits.subcategory[this.fields.subcategory].min_adjustment;
      }
      return this.expenseNumeric >= this.limits.expense_min_adjustment;
    },

  },

  methods: {

    minimumAdjustmentMessage(minimum) {
      // Negative minimum:
      return minimum < 0 ? 'the negative initial reserve amount cannot exceed ' + (-minimum) + '.' :
        // Zero minimum:
        (minimum === 0) ? 'the initial reserve amount cannot be negative.' :
          // Positive minimum:
          'the initial reserve amount must be at least ' + (minimum) + '.';
    },

    onModalShown() {
      this.validationDisplayed = false;
      cims.addDateTimePickers();
    },

  },
}
</script>
